import type { User, Session } from '@supabase/supabase-js';
import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';

import { supabase } from '../../lib/supabase';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAuthenticated: boolean;
  loading: boolean;
  signOut: () => Promise<void>;
  /**
   * Refresh the JWT session to get updated claims (company_id, user_role).
   * Call this after:
   * - Creating a new company
   * - Switching companies
   * - Any operation that changes the user's company context
   */
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

// Session validation interval (check every 5 minutes)
const SESSION_VALIDATION_INTERVAL = 5 * 60 * 1000;

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const validationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const checkSessionValidityRef = useRef<(() => Promise<void>) | undefined>(undefined);

  // Validate that the user still exists in the database
  const validateUserExists = useCallback(async (userId: string): Promise<boolean> => {
    try {
      // First, check if the user still exists in auth.users via getUser()
      // This validates the JWT token and checks if the user exists in Supabase Auth
      // Add timeout to prevent hanging
      const authUserPromise = supabase.auth.getUser();
      const authUserTimeout = new Promise<{ data: { user: null }; error: Error }>((_, reject) => 
        setTimeout(() => reject(new Error('getUser timeout')), 5000)
      );
      
      let authResult;
      try {
        authResult = await Promise.race([
          authUserPromise,
          authUserTimeout
        ]);
      } catch (error) {
        // Timeout or network error occurred - don't sign out on network issues
        const errorMessage = error instanceof Error ? error.message : String(error);
        const isNetworkError = errorMessage.includes('Failed to fetch') || 
                               errorMessage.includes('ERR_CONNECTION_REFUSED') ||
                               errorMessage.includes('NetworkError') ||
                               errorMessage.includes('timeout');
        
        if (isNetworkError) {
          console.warn('[AuthProvider] Network error during getUser, assuming user is valid:', errorMessage);
          return true; // Don't sign out on network errors
        }
        
        // Other errors (timeout) - assume user is valid to prevent blocking
        console.warn('[AuthProvider] getUser error, assuming user is valid:', errorMessage);
        return true;
      }
      
      // Check if error is a network error vs actual auth error
      if (authResult.error) {
        const errorMessage = authResult.error.message || '';
        const isNetworkError = errorMessage.includes('Failed to fetch') || 
                               errorMessage.includes('ERR_CONNECTION_REFUSED') ||
                               errorMessage.includes('NetworkError') ||
                               errorMessage.includes('timeout');
        
        if (isNetworkError) {
          console.warn('[AuthProvider] Network error during getUser, assuming user is valid:', errorMessage);
          return true; // Don't sign out on network errors
        }
        
        // Actual auth error (user doesn't exist, invalid token, etc.)
        console.warn('[AuthProvider] User does not exist in auth system:', errorMessage);
        return false;
      }
      
      if (!authResult.data?.user || authResult.data.user.id !== userId) {
        console.warn('[AuthProvider] User mismatch or missing in auth system');
        return false;
      }

      // If getUser() succeeds, the user exists in auth.users
      // Now verify they have data in the database by checking their relationships
      // This helps catch cases where auth.users still has the user but database was reset
      // Add timeout to prevent hanging
      const relationshipsPromise = supabase
        .from('user_company_relationships')
        .select('id')
        .eq('user_id', userId)
        .limit(1);
      
      const relationshipsTimeout = new Promise<{ data: null; error: Error }>((_, reject) => 
        setTimeout(() => reject(new Error('relationships query timeout')), 5000)
      );

      let relationshipsResult;
      try {
        relationshipsResult = await Promise.race([
          relationshipsPromise,
          relationshipsTimeout
        ]);
      } catch (error) {
        // Timeout or network error occurred - user exists in auth, so assume valid
        const errorMessage = error instanceof Error ? error.message : String(error);
        const isNetworkError = errorMessage.includes('Failed to fetch') || 
                               errorMessage.includes('ERR_CONNECTION_REFUSED') ||
                               errorMessage.includes('NetworkError') ||
                               errorMessage.includes('timeout');
        
        if (isNetworkError) {
          console.warn('[AuthProvider] Network error during relationships query, assuming user is valid:', errorMessage);
        } else {
          console.warn('[AuthProvider] Relationships query timeout, assuming user is valid');
        }
        return true;
      }

      // If there's an error querying relationships, it might be an RLS issue, timeout, or network error
      // But if getUser() succeeds, the user at least exists in auth
      // We'll be lenient here - if getUser() succeeds, consider user valid
      // The periodic check will catch stale sessions
      if (relationshipsResult.error) {
        const errorMessage = relationshipsResult.error.message || '';
        const isNetworkError = errorMessage.includes('Failed to fetch') || 
                               errorMessage.includes('ERR_CONNECTION_REFUSED') ||
                               errorMessage.includes('NetworkError');
        
        if (isNetworkError) {
          console.warn('[AuthProvider] Network error checking user relationships, assuming user is valid:', errorMessage);
        } else {
          console.warn('[AuthProvider] Error checking user relationships (non-network):', relationshipsResult.error);
          // User exists in auth but might not have relationships yet or query timed out
          // This is okay for new users, so return true
        }
        return true;
      }

      // If we got data, user definitely exists and has relationships
      return true;
    } catch (error) {
      // Check if this is a network error
      const errorMessage = error instanceof Error ? error.message : String(error);
      const isNetworkError = errorMessage.includes('Failed to fetch') || 
                             errorMessage.includes('ERR_CONNECTION_REFUSED') ||
                             errorMessage.includes('NetworkError') ||
                             errorMessage.includes('timeout');
      
      if (isNetworkError) {
        console.warn('[AuthProvider] Network error during validation, assuming user is valid:', errorMessage);
      } else {
        console.error('[AuthProvider] Error validating user existence:', error);
      }
      
      // On network errors or other errors, assume user is valid to prevent blocking
      // This prevents signing out users when Supabase is temporarily unavailable
      return true;
    }
  }, []);

  // Check session validity and sign out if invalid
  const checkSessionValidity = useCallback(async () => {
    const { data: { session: currentSession } } = await supabase.auth.getSession();
    
    if (!currentSession?.user) {
      // No session, clear state
      setSession(null);
      setUser(null);
      return;
    }

    // Validate user still exists
    const userExists = await validateUserExists(currentSession.user.id);
    
    if (!userExists) {
      console.warn('[AuthProvider] User no longer exists, signing out...');
      await supabase.auth.signOut();
      setSession(null);
      setUser(null);
      return;
    }

    // Session is valid, update state
    setSession(currentSession);
    setUser(currentSession.user);
  }, [validateUserExists]);

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          console.error('[AuthProvider] Error getting session:', error);
          setSession(null);
          setUser(null);
          setLoading(false);
        } else if (session?.user) {
          // Set session immediately to prevent blocking the UI
          // Validate user exists asynchronously after setting session
          setSession(session);
          setUser(session.user);
          setLoading(false);
          
          // Refresh session to get new JWT with company_id claim
          // This ensures RLS policies work correctly with the new JWT-based system
          try {
            const { error: refreshError } = await supabase.auth.refreshSession();
            if (refreshError) {
              console.warn('[AuthProvider] Failed to refresh session on init:', refreshError);
            } else {
              // Session refreshed successfully with company_id claim
            }
          } catch (err) {
            console.warn('[AuthProvider] Error refreshing session on init:', err);
            // Non-fatal - JWT will update on next request
          }
          
          // Validate user exists in the background
          validateUserExists(session.user.id).then((userExists) => {
            if (!userExists) {
              console.warn('[AuthProvider] Initial session user does not exist, signing out...');
              supabase.auth.signOut();
              setSession(null);
              setUser(null);
            }
          }).catch((error) => {
            console.error('[AuthProvider] Error validating initial session:', error);
            // Don't sign out on validation error - user is already signed in
          });
        } else {
          setSession(null);
          setUser(null);
          setLoading(false);
        }
      } catch (error) {
        console.error('[AuthProvider] Error in getInitialSession:', error);
        setSession(null);
        setUser(null);
        setLoading(false);
      }
    };

    getInitialSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        // Only log meaningful auth events, skip INITIAL_SESSION when no session exists
        if (event !== 'INITIAL_SESSION' || session?.user) {
          console.warn('[AuthProvider] Auth state changed:', event, session?.user?.email);
        }
        
        if (event === 'SIGNED_OUT') {
          setSession(null);
          setUser(null);
          setLoading(false);
          return;
        }

        // USER_UPDATED events are just metadata changes (e.g., EULA acceptance)
        // Don't validate user existence for these - user is already authenticated
        if (event === 'USER_UPDATED' && session?.user) {
          setSession(session);
          setUser(session.user);
          setLoading(false);
          return;
        }

        if (session?.user) {
          // Set session immediately to prevent blocking the UI
          // Validate user exists asynchronously after setting session
          setSession(session);
          setUser(session.user);
          setLoading(false);
          
          // Validate user exists in the background (only for SIGNED_IN, TOKEN_REFRESHED, etc.)
          validateUserExists(session.user.id).then((userExists) => {
            if (!userExists) {
              console.warn('[AuthProvider] Auth state change: user does not exist, signing out...');
              supabase.auth.signOut();
              setSession(null);
              setUser(null);
            }
          }).catch((error) => {
            console.error('[AuthProvider] Error validating user after sign-in:', error);
            // Don't sign out on validation error - user is already signed in
          });
        } else {
          setSession(null);
          setUser(null);
          setLoading(false);
        }
      }
    );

    // Set up periodic session validation
    // Use a ref to store the latest checkSessionValidity function to avoid recreating interval
    checkSessionValidityRef.current = checkSessionValidity;
    
    validationIntervalRef.current = setInterval(() => {
      checkSessionValidityRef.current?.();
    }, SESSION_VALIDATION_INTERVAL);

    return () => {
      subscription.unsubscribe();
      if (validationIntervalRef.current) {
        clearInterval(validationIntervalRef.current);
      }
    };
  }, [validateUserExists, checkSessionValidity]);

  /**
   * Refresh the JWT session to get updated claims (company_id, user_role).
   * This should be called after:
   * - Creating a new company
   * - Switching companies via last_selected_company
   * - Any operation that changes the user's company context
   */
  const refreshSession = useCallback(async () => {
    const { data, error } = await supabase.auth.refreshSession();
    
    if (error) {
      // If refresh fails and we have no session, sign out
      if (!data?.session) {
        await supabase.auth.signOut();
        setSession(null);
        setUser(null);
      }
      throw error;
    }
    
    if (data?.session) {
      setSession(data.session);
      setUser(data.session.user);
    } else {
      // No session returned - user is likely signed out
      await supabase.auth.signOut();
      setSession(null);
      setUser(null);
    }
  }, []);

  const signOut = async () => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('[AuthProvider] Error signing out:', error);
        throw error;
      }
      setSession(null);
      setUser(null);
    } catch (error) {
      console.error('[AuthProvider] Sign out error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    session,
    isAuthenticated: !!user,
    loading,
    signOut,
    refreshSession,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
